import {
  createTheme,
  alpha,
  styled,
  PaletteOptions,
} from "@mui/material/styles";
import ApartmentIcon from "@mui/icons-material/Apartment"; // Added import
import AddIcon from "@mui/icons-material/Add"; // Added import
import GroupIcon from "@mui/icons-material/Group"; // Added import

// Define spacing constants
const spacing = {
  xs: "4px", // 0.5x base unit
  sm: "8px", // 1x base unit
  md: "16px", // 2x base unit
  lg: "24px", // 3x base unit
  xl: "32px", // 4x base unit
};

// Define dimension constants
const dimensions = {
  textField: {
    width: 350,
  },
  card: {
    small: {
      height: 50,
      width: "25%",
    },
    medium: {
      height: 70,
      width: "30%",
    },
    large: {
      height: 80,
      width: "95%",
    },
  },
  drawer: {
    width: 240,
  },
  modal: {
    width: 400,
  },
};

// Define typography constants
const typography = {
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(","),
  h1: {
    fontSize: "2.5rem",
    fontWeight: 500,
  },
  h2: {
    fontSize: "2rem",
    fontWeight: 500,
  },
  h3: {
    fontSize: "1.75rem",
    fontWeight: 500,
  },
  h4: {
    fontSize: "1.5rem",
    fontWeight: 500,
  },
  h5: {
    fontSize: "1.25rem",
    fontWeight: 500,
  },
  h6: {
    fontSize: "1rem",
    fontWeight: 500,
  },
  body1: {
    fontSize: "1rem",
  },
  body2: {
    fontSize: "0.875rem",
  },
};

// Define color palette
const palette: PaletteOptions = {
  primary: {
    main: "#ce0e29",
    light: "#f44336",
    dark: "#b71c1c",
    contrastText: "#fff",
  },
  secondary: {
    main: "#757575",
    light: "#9e9e9e",
    dark: "#616161",
    contrastText: "#fff",
  },
  info: {
    main: "#4334c9",
    light: "#9d95e4",
    dark: "#221a69",
    contrastText: "#fff",
  },
  error: {
    main: "#d32f2f",
    light: "#ef5350",
    dark: "#c62828",
    contrastText: "#fff",
  },
  warning: {
    main: "#ed6c02",
    light: "#ff9800",
    dark: "#e65100",
    contrastText: "#fff",
  },
  success: {
    main: "#2e7d32",
    light: "#4caf50",
    dark: "#1b5e20",
    contrastText: "#fff",
  },
  grey: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
    A100: "#f5f5f5",
    A200: "#eeeeee",
    A400: "#bdbdbd",
    A700: "#616161",
  },
};

export const baseTheme = createTheme({
  typography,
  palette,
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: spacing.md,
          paddingRight: spacing.md,
          display: "flex", // Added for centering children
          flexDirection: "column", // Added for centering children
          alignItems: "center", // Added for centering children
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&.dropdown-item": {
            color: "inherit",
            width: "100%",
            textAlign: "left",
            justifyContent: "flex-start",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          maxHeight: "90vh",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: "black",
          color: "white",
          height: "61px",
          padding: "16px 24px",
          fontSize: "1.50rem",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "32px",
          marginTop: "10px",
          overflow: "auto",
          maxHeight: "calc(90vh - 170px)",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "8px 24px 16px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: dimensions.textField.width,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: `1px solid ${palette.grey?.[200]}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.md,
          "&:last-child": {
            paddingBottom: spacing.md,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: dimensions.drawer.width,
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: spacing.sm,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: spacing.sm,
        },
      },
    },
  },
});

export const Container = styled("div")(({ theme }) => ({
  display: "inline-block",
  backgroundColor: theme?.palette.grey[300],
  paddingTop: 10,
  paddingBottom: 10,
  maxWidth: "100%!important",
}));

export const ListContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme?.spacing(2),
  width: "100%",
  padding: theme?.spacing(2.5),
  backgroundColor: theme?.palette.grey[300],
  borderRadius: "4px",
  marginTop: theme?.spacing(2),
}));

export const SectionWrapper = styled("div")(() => ({
  width: "100%",
}));

export const AddButton = styled("div")(({ theme }) => ({
  marginBottom: theme?.spacing(2),
}));

export const InlineFormContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme?.spacing(2),
  marginBottom: theme?.spacing(2),
}));

export const Card = styled("div")(({ theme }) => ({
  padding: theme?.spacing(2),
  backgroundColor: theme.palette.background.paper, // Standard white
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start", // Content within CardIcon and CardTitle handles its own centering
  borderRadius: theme?.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[400]}`,
}));

export const UserEmail = styled("h5")(({ theme }) => ({
  marginTop: 0,
  marginLeft: 0,
  fontSize: theme?.typography.body1.fontSize,
  wordBreak: "break-all",
  marginBottom: theme?.spacing(1),
}));

export const FormWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: theme?.spacing(1),
  minHeight: "40px",
  marginBottom: theme?.spacing(5.625),
}));

export const ButtonGroup = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "-45px",
  left: 0,
  display: "flex",
  gap: theme?.spacing(1.25),
}));

export const Divider = styled("div")(({ theme }) => ({
  margin: `${theme?.spacing(3)} 0`,
  backgroundColor: theme?.palette.grey[200],
  height: "2px",
  width: "100%",
}));

export const SectionContainer = styled("div")(({ theme }) => ({
  width: "100%",
  padding: `${theme?.spacing(3)} 0`,
}));

export const SectionTitle = styled("div")(({ theme }) => ({
  marginBottom: theme?.spacing(3),
}));

export const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme?.spacing(1.25),
  marginTop: theme?.spacing(2),
}));

// Helper function to get disabled button background color
export const getDisabledBackgroundColor = (
  theme: any,
  isDisabled?: boolean,
) => {
  if (!theme) return undefined;
  return isDisabled
    ? alpha(theme.palette.primary.light, 0.3)
    : theme.palette.primary.main;
};

export const LogoImg = styled("img")(() => ({
  width: "min-content",
  marginTop: "10vh",
}));

export const SearchWrapper = styled("div")(() => ({
  width: "40%",
  marginTop: "5vh",
}));

export const HotelCardsGrid = styled("div")(({ theme }) => ({
  width: "100%",
  padding: "0 5vw 0 5vw",
  display: "grid",
  gridGap: theme.spacing(2.5),
  marginTop: theme.spacing(7.5),
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
  marginBottom: theme.spacing(2.5),
}));

export const CardIcon = styled(ApartmentIcon)(({ theme }) => ({
  fontSize: "50px !important",
  display: "block",
  margin: `0 auto ${theme.spacing(1.25)} auto`,
  "@media (max-height: 600px)": {
    fontSize: "40px !important",
  },
}));

export const AddCardIcon = styled(AddIcon)(({ theme }) => ({
  fontSize: "50px !important",
  display: "block",
  margin: `0 auto ${theme.spacing(1.25)} auto`, // 10px bottom margin
  "@media (max-height: 600px)": {
    fontSize: "40px !important",
  },
}));

export const UsersIcon = styled(GroupIcon)(({ theme }) => ({
  fontSize: "50px !important",
  display: "block",
  margin: `0 auto ${theme.spacing(1.25)} auto`, // 10px bottom margin
  "@media (max-height: 600px)": {
    fontSize: "40px !important",
  },
}));

export const CardTitle = styled("h5")(({ theme }) => ({
  ...theme.typography.h5, // Apply h5 typography from theme
  color: theme.palette.grey[700],
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  width: "100%",
  textAlign: "center",
  margin: 0, // Reset default margin for h5 if needed after theme application
  "@media (max-height: 600px)": {
    // Adjust if needed based on icon size change
  },
}));
